/**
 * The tabbed record (docs/features/one-record-layout/01_mock_up.md, a mock-up approved by Ben on 5 Nov): a short header
 * (title, states, up to five key facts, the record's own commands and one grouped More…), then one card of tabs, Details
 * first and then a tab per child table and related list. Pure: the page draws what these return.
 *
 * Where each command lives is derived from what the manifest already carries (`commandHomesOf`), with a per-object
 * override (`ManifestRecordLayout.places`) for the commands it places badly. The declarations a record layout needs
 * (`key_facts`, `opens_on`, `sections`, `places`) are proposed: only mock-up drafts supply them today.
 */
import type {
  Command,
  CommandId,
  ManifestCommand,
  ManifestCommandPlace,
  ManifestObject,
  ManifestRecordLayout,
  ObjectId,
} from "./contract";
import type { ManifestIndex } from "./manifest";
import type { PresentedFact, PresentedFactGroup } from "./record";
import {
  headerBlockedLeadOf,
  headerMainOf,
  ownerHasSpoken,
} from "./row-commands";
import { blockedLayoutOf } from "./shared-reasons";

/** The most key facts a header shows. */
export const KEY_FACTS_SHOWN = 5;
/** The record's own commands a desktop header draws as buttons, the main one included; the rest are in More…. */
export const HEADER_BUTTONS_SHOWN = 3;
/** The commands a desktop tab draws as buttons at its head; the rest are in its More…. */
export const TAB_BUTTONS_SHOWN = 4;
/** The Details tab's id and its name. */
export const DETAILS_TAB = "details";
export const DETAILS_LABEL = "Details";

// ── Where a command lives ────────────────────────────────────────────────

export type CommandHome =
  /** The record's own: a header button or More…; `isLast` sets it apart last in More…, in the destructive colour. */
  | { kind: "header"; isLast: boolean }
  /** On the tab of the child table or related list (by object id) whose rows it creates or changes. */
  | { kind: "tab"; tab: ObjectId }
  /** The pencil (or switch) beside the one fact it sets, on Details, and nowhere else. */
  | { kind: "setting"; field: string };

/**
 * The verbs of commands that end, withdraw or take something away from the record (Cancel order…, Discard…, Archive):
 * set apart last in More…. The manifest's `destructive` means "confirm first", which everyday commands carry (Add
 * catalogue item…, Release…), so it cannot say this; until the contract carries a mark of its own (a gap the mock-up
 * names), the command's verb does.
 */
const ENDING_VERBS = new Set([
  "Abandon",
  "Anonymise",
  "Archive",
  "Cancel",
  "Close",
  "Deactivate",
  "Decline",
  "Delete",
  "Disconnect",
  "Discard",
  "Dismiss",
  "End",
  "Fold",
  "Remove",
  "Retire",
  "Revoke",
  "Stop",
  "Suspend",
  "Unlink",
  "Void",
  "Withdraw",
]);

/** Whether a header command ends or takes away from the record, by its verb (see `ENDING_VERBS`). */
export function isEndingCommand(
  declared: Pick<ManifestCommand, "label">,
): boolean {
  const verb = declared.label.split(/\s+/)[0]?.replace(/…$|\.\.\.$/, "") ?? "";
  return ENDING_VERBS.has(verb);
}

export interface PlacementInput {
  object: ManifestObject;
  /** The record's commands, and the collection commands its related lists offer on it. */
  commands: readonly Command[];
  /** A related list's collection command, by name: the related object it is offered for. */
  offeredFor?: ReadonlyMap<CommandId, ObjectId>;
  /** The child objects the record draws (those it sends as null do not apply and are left out). */
  childObjects: readonly ObjectId[];
  /** The related objects the record draws. */
  relatedObjects: readonly ObjectId[];
  /** The record's facts, by field. */
  factFields: readonly string[];
}

const COLLECTION = "collection:";

/**
 * Where each command lives (the mock-up's rule, in order):
 * 1. the object's override (`layout.places`), where it names a tab the record draws or a fact it has;
 * 2. a command that declares `edits` is its fact's setting;
 * 3. the record's main command (`main: true`, or the owner's `main` on this record) stays in the header;
 * 4. a command on a child object, or one that creates a child row (`collection:<child>`), lives on that child's tab;
 * 5. a collection command a related list offers, or one presented on the record that creates a record of a related
 *    list's object (New draft quote… on a customer with Quotes), lives on that list's tab;
 * 6. everything else is the record's own, in the header, set apart last when it ends the record (`isEndingCommand`).
 *
 * The manifest does not say which child a record command adds to or changes (Add catalogue item… on an order targets
 * the order), nor which fact a command that does more than set it changes (Change trading company…): those need the
 * override, and are what the contract would have to carry (`acts_on`).
 */
export function commandHomesOf(
  index: ManifestIndex,
  input: PlacementInput,
): Map<CommandId, CommandHome> {
  const layout = input.object.record?.layout;
  const children = new Set(input.childObjects);
  const related = new Set(input.relatedObjects);
  const facts = new Set(input.factFields);
  const homes = new Map<CommandId, CommandHome>();
  for (const command of input.commands) {
    const declared = index.commandOf(command.name);
    const override = layout?.places?.[command.name];
    const placed = override
      ? placeOf(override, declared, children, related, facts)
      : null;
    homes.set(
      command.name,
      placed ??
        derivedHomeOf(declared, command, input, children, related, facts),
    );
  }
  return homes;
}

function placeOf(
  place: ManifestCommandPlace,
  declared: ManifestCommand,
  children: ReadonlySet<ObjectId>,
  related: ReadonlySet<ObjectId>,
  facts: ReadonlySet<string>,
): CommandHome | null {
  if (place === "header")
    return { kind: "header", isLast: isEndingCommand(declared) };
  if (place === "header_last") return { kind: "header", isLast: true };
  if ("tab" in place)
    return children.has(place.tab) || related.has(place.tab)
      ? { kind: "tab", tab: place.tab }
      : null;
  return facts.has(place.setting)
    ? { kind: "setting", field: place.setting }
    : null;
}

function derivedHomeOf(
  declared: ManifestCommand,
  command: Command,
  input: PlacementInput,
  children: ReadonlySet<ObjectId>,
  related: ReadonlySet<ObjectId>,
  facts: ReadonlySet<string>,
): CommandHome {
  const edited = declared.edits?.fields.find((field) => facts.has(field));
  if (edited) return { kind: "setting", field: edited };
  const isMain =
    command.main === true ||
    (!ownerHasSpoken([command]) &&
      declared.main === true &&
      declared.target === input.object.id);
  if (isMain) return { kind: "header", isLast: false };
  const target = declared.target.startsWith(COLLECTION)
    ? declared.target.slice(COLLECTION.length)
    : declared.target;
  if (children.has(target)) return { kind: "tab", tab: target };
  const offered = input.offeredFor?.get(command.name);
  if (offered && related.has(offered)) return { kind: "tab", tab: offered };
  if (declared.target.startsWith(COLLECTION) && related.has(target))
    return { kind: "tab", tab: target };
  return { kind: "header", isLast: isEndingCommand(declared) };
}

/** The fields a command sets that the manifest does not declare as `edits`: the overrides that make it a setting. */
export function overriddenEditsOf(
  homes: ReadonlyMap<CommandId, CommandHome>,
  index: ManifestIndex,
): Map<CommandId, string[]> {
  const extra = new Map<CommandId, string[]>();
  for (const [name, home] of homes)
    if (home.kind === "setting" && !index.commandOf(name).edits)
      extra.set(name, [home.field]);
  return extra;
}

// ── Key facts and Details ────────────────────────────────────────────────

/** The header's key facts: the ones the object declares, in its order, at most five; none where it declares none. */
export function keyFactsOf(
  facts: readonly PresentedFact[],
  layout: ManifestRecordLayout | undefined,
): PresentedFact[] {
  const byField = new Map(facts.map((f) => [f.field, f]));
  return (layout?.key_facts ?? [])
    .flatMap((field) => {
      const fact = byField.get(field);
      return fact ? [fact] : [];
    })
    .slice(0, KEY_FACTS_SHOWN);
}

export interface DetailsSection {
  label: string;
  facts: PresentedFact[];
  /** Starts folded, with `summary` in its heading. */
  isFolded: boolean;
  /** "9 settings", "3 details": what the section holds, shown while it is folded. */
  summary: string;
}

/** A section a fact group names that holds more settings than this starts folded (a module's sign-in settings). */
const FOLD_SETTINGS_OVER = 4;

/**
 * Details: every fact that is not a key fact, and every setting (a key fact a command sets keeps its pencil here), in
 * named sections, one per line. A fact goes to the section the object's layout names it in, else its fact group's
 * section, else its fact group's label, else the first section, "Details". Sections come in that order: Details, the
 * layout's, then the fact groups'. An empty fact that is not a setting is left out, as the header leaves it out today,
 * and a section left with nothing is not drawn.
 */
export function detailsSectionsOf(
  facts: readonly PresentedFact[],
  groups: readonly PresentedFactGroup[],
  layout: ManifestRecordLayout | undefined,
  isSetting: (field: string) => boolean,
): DetailsSection[] {
  const key = new Set(layout?.key_facts?.slice(0, KEY_FACTS_SHOWN) ?? []);
  const isShown = (fact: PresentedFact) =>
    isSetting(fact.field) || (!key.has(fact.field) && !fact.isEmpty);
  const declared = layout?.sections ?? [];
  const sectionOfField = new Map<string, string>();
  const fromGroup = new Set<string>();
  for (const section of declared)
    for (const field of section.fields)
      if (!sectionOfField.has(field)) sectionOfField.set(field, section.label);
  for (const group of groups) {
    const label = group.section ?? group.label;
    if (!label) continue;
    if (group.section) fromGroup.add(label);
    for (const field of group.fields)
      if (!sectionOfField.has(field)) sectionOfField.set(field, label);
  }
  const order = [
    DETAILS_LABEL,
    ...declared.map((s) => s.label),
    ...groups.flatMap((g) => {
      const label = g.section ?? g.label;
      return label ? [label] : [];
    }),
  ].filter((label, i, all) => all.indexOf(label) === i);
  const byField = new Map(facts.map((f) => [f.field, f]));
  // Facts in a declared section keep the section's order; every other fact keeps the record's.
  const membersOf = (label: string): PresentedFact[] => {
    const section = declared.find((s) => s.label === label);
    const ordered = section
      ? section.fields.flatMap((f) => {
          const fact = byField.get(f);
          return fact && sectionOfField.get(f) === label ? [fact] : [];
        })
      : facts.filter(
          (f, i) =>
            (sectionOfField.get(f.field) ?? DETAILS_LABEL) === label &&
            facts.findIndex((g) => g.field === f.field) === i,
        );
    return ordered.filter(isShown);
  };
  return order.flatMap((label) => {
    const members = membersOf(label);
    if (members.length === 0) return [];
    const settings = members.filter((f) => isSetting(f.field)).length;
    const section = declared.find((s) => s.label === label);
    const isFolded =
      section?.folded ??
      (fromGroup.has(label) && settings > FOLD_SETTINGS_OVER);
    return [
      {
        label,
        facts: members,
        isFolded,
        summary:
          settings > 0
            ? `${settings} ${settings === 1 ? "setting" : "settings"}`
            : `${members.length} ${members.length === 1 ? "detail" : "details"}`,
      },
    ];
  });
}

// ── Tabs ────────────────────────────────────────────────────────────────

export interface RecordTab {
  /** `details`, or the child or related object's id. */
  id: string;
  /** How the tab is named in the link (`?tab=lines`). */
  slug: string;
  label: string;
  kind: "details" | "child" | "related";
  /** How many rows; null for Details, and for a related list still being read. */
  count: number | null;
  /** Holds no rows: after the others, and only ever under More ▾ (`tabStripOf`). */
  isEmpty: boolean;
}

export interface TabSource {
  object: ObjectId;
  label: string;
  /** The child's record-view field (a child table's tab is named by it in the link: `lines`). */
  field?: string;
  /** Rows in all; null while a related list is being read. */
  total: number | null;
}

function kebab(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Details, always first, then a tab per child table and related list with rows, then those with none (which the bar
 * puts under More ▾, `tabStripOf`), each group in the record's tab order: the order the object declares (`layout.tabs`,
 * Ben 5 Nov: what the record is made of, what happens to it next in the order it happens, who and where, checks and
 * history, Emails last), then the lists it does not name, in the order the object lists them (children, then related
 * lists), which is every list's order where it declares none. A related list still being read keeps its place among
 * those with rows until it answers.
 */
export function recordTabsOf(
  children: readonly TabSource[],
  related: readonly TabSource[],
  order: readonly ObjectId[] = [],
): RecordTab[] {
  const used = new Set<string>([DETAILS_TAB]);
  const slugOf = (source: TabSource) => {
    const wanted = kebab(source.field ?? source.label) || kebab(source.object);
    const slug = used.has(wanted) ? kebab(source.object) : wanted;
    used.add(slug);
    return slug;
  };
  const listed = [
    ...children.map((c) => ({ ...c, kind: "child" as const })),
    ...related.map((r) => ({ ...r, kind: "related" as const })),
  ].map((source): RecordTab => ({
    id: source.object,
    slug: slugOf(source),
    label: source.label,
    kind: source.kind,
    count: source.total,
    isEmpty: source.total === 0,
  }));
  const declared = order.flatMap((id) => listed.filter((t) => t.id === id));
  const tabs = [...declared, ...listed.filter((t) => !declared.includes(t))];
  return [
    {
      id: DETAILS_TAB,
      slug: DETAILS_TAB,
      label: DETAILS_LABEL,
      kind: "details",
      count: null,
      isEmpty: false,
    },
    ...tabs.filter((t) => !t.isEmpty),
    ...tabs.filter((t) => t.isEmpty),
  ];
}

/**
 * The tab the record shows: the one the link names (`?tab=`), else the one the object opens on (`layout.opens_on`)
 * while it has rows (an order with lines opens on them; one with none yet on Details), else Details.
 */
export function openTabOf(
  tabs: readonly RecordTab[],
  param: string | null,
  layout: ManifestRecordLayout | undefined,
): RecordTab {
  return (
    tabs.find((t) => param !== null && t.slug === param) ??
    tabs.find((t) => t.id === layout?.opens_on && !t.isEmpty) ??
    tabs[0]!
  );
}

/** The tab bar as drawn (the "priority+" pattern): the tabs shown, then a final More ▾ holding the rest. */
export interface TabStrip {
  /** Drawn as tabs, in order: Details first, then the lists with rows that fit. */
  shown: RecordTab[];
  /** In More ▾, first: the lists with rows that didn't fit, in order. */
  overflow: RecordTab[];
  /** In More ▾, after a separator: every list with no rows, in order. Never a tab of its own. */
  empty: RecordTab[];
}

/**
 * Which tabs show and which go under More ▾. Details is always the first tab. The lists with rows show as tabs while
 * `fit` of them fit (a phone passes Infinity: its tabs scroll sideways); the rest go under More ▾, in order, before the
 * empty lists, which are always under More ▾. A list with rows that is open but would not fit swaps in for the last
 * shown, so the open tab is always in view; an empty list that is open stays under More ▾, which then names it.
 */
export function tabStripOf(
  tabs: readonly RecordTab[],
  fit: number,
  open: RecordTab,
): TabStrip {
  const details = tabs.filter((t) => t.kind === "details");
  const withRows = tabs.filter((t) => t.kind !== "details" && !t.isEmpty);
  const empty = tabs.filter((t) => t.kind !== "details" && t.isEmpty);
  const count = Math.max(0, fit);
  let shown = withRows.slice(0, count);
  if (withRows.includes(open) && !shown.includes(open))
    shown = [...shown.slice(0, Math.max(0, count - 1)), open];
  return {
    shown: [...details, ...shown],
    overflow: withRows.filter((t) => !shown.includes(t)),
    empty,
  };
}

/**
 * How many of the lists with rows fit beside Details on one line, measured: the most whose widths, with Details' and
 * the gaps between them, leave room for More ▾ wherever More ▾ is still needed (an empty list, or a list that didn't
 * fit). Infinity when nothing is measured yet, so the first draw shows every tab.
 */
export function tabsThatFit({
  available,
  detailsWidth,
  widths,
  moreWidth,
  hasEmpty,
}: {
  available: number | null;
  detailsWidth: number;
  /** Each list with rows' tab, in order. */
  widths: readonly number[];
  moreWidth: number;
  hasEmpty: boolean;
}): number {
  if (available === null || available <= 0) return Infinity;
  for (let k = widths.length; k > 0; k--) {
    const needsMore = hasEmpty || k < widths.length;
    const used =
      detailsWidth +
      widths.slice(0, k).reduce((sum, w) => sum + w, 0) +
      (needsMore ? moreWidth : 0);
    if (used <= available) return k;
  }
  return 0;
}

// ── Commands in the header, its More… and on a tab ───────────────────────

export interface HeaderMenu {
  /** The main command, primary, while it can run. */
  primary: Command | null;
  /** Drawn as buttons: the primary first. */
  buttons: Command[];
  /** The main command's block (or the reason it shares), said above the buttons. */
  said: { label: string | null; reason: string } | null;
  /** More…'s groups, in order. */
  overflow: Command[];
  /** Each tab's commands that can run, under the tab's name: More… is an index of everything the record can do. */
  byTab: { tab: RecordTab; commands: Command[] }[];
  /** Set apart last, in the destructive colour. */
  last: Command[];
  /** "Not available now", each with its reason; a reason several share is said once (`sharedSaid`). */
  unavailable: Command[];
  /** Reasons that block several commands, said once at the head of "Not available now". */
  sharedSaid: string[];
  /** Every reason several commands share, so an item leaves out one said once. */
  sharedReasons: string[];
}

export function hasMoreMenu(menu: HeaderMenu): boolean {
  return (
    menu.overflow.length > 0 ||
    menu.byTab.length > 0 ||
    menu.last.length > 0 ||
    menu.unavailable.length > 0
  );
}

/**
 * The header's commands and its grouped More… (the mock-up's rule): the main command primary while it can run, or its
 * block said above the buttons (`headerBlockedLeadOf`); the record's other commands as buttons (three in all on a
 * desktop; on a phone the main one only, none when the owner says none leads); More… holds (a) the header commands
 * that didn't fit, (b) every tab's commands under the tab's name, (c) the commands that end the record, set apart, and
 * (d) "Not available now" with reasons. A setting is its pencil and is in none of these.
 */
export function headerMenuOf({
  own,
  alongside = [],
  homes,
  tabs,
  declaredMain,
  isPhone,
}: {
  /** The record's own commands (its object's and those the record presents), in the owner's order. */
  own: readonly Command[];
  /** The related lists' collection commands, after the record's own; none leads. */
  alongside?: readonly Command[];
  homes: ReadonlyMap<CommandId, CommandHome>;
  tabs: readonly RecordTab[];
  declaredMain: string | null;
  isPhone: boolean;
}): HeaderMenu {
  const homeOf = (c: Command): CommandHome =>
    homes.get(c.name) ?? { kind: "header", isLast: false };
  const inHeader = (c: Command) => homeOf(c).kind === "header";
  const isLast = (c: Command) => {
    const home = homeOf(c);
    return home.kind === "header" && home.isLast;
  };
  const header = own.filter(inHeader);
  const headerAlongside = alongside.filter(inHeader);
  const primary = headerMainOf(header, declaredMain);
  const blockedLead = headerBlockedLeadOf(header, declaredMain);
  const runnable = [
    ...(primary ? [primary] : []),
    ...header.filter((c) => c.enabled && c !== primary && !isLast(c)),
    ...headerAlongside.filter((c) => c.enabled && !isLast(c)),
  ];
  const ownerSaidNoneLeads = !primary && ownerHasSpoken(header);
  const shownCount = isPhone
    ? ownerSaidNoneLeads
      ? 0
      : 1
    : HEADER_BUTTONS_SHOWN;
  const buttons = runnable.slice(0, shownCount);
  const tabCommands = [...own, ...alongside].filter(
    (c) => homeOf(c).kind === "tab",
  );
  const byTab = tabs.flatMap((tab) => {
    const commands = tabCommands.filter((c) => {
      const home = homeOf(c);
      return home.kind === "tab" && home.tab === tab.id && c.enabled;
    });
    return commands.length > 0 ? [{ tab, commands }] : [];
  });
  const { said, folded, sharedReasons, foldedShared } = blockedLayoutOf(
    [...header, ...headerAlongside, ...tabCommands],
    blockedLead,
  );
  return {
    primary,
    buttons,
    said,
    overflow: runnable.slice(buttons.length),
    byTab,
    last: [...header, ...headerAlongside].filter(
      (c) => c.enabled && c !== primary && isLast(c),
    ),
    unavailable: folded,
    sharedSaid: foldedShared,
    sharedReasons,
  };
}

export interface TabHeadCommands {
  buttons: Command[];
  more: Command[];
  unavailable: Command[];
}

/** A tab's commands at its head: those that can run as buttons (four on a desktop, the first on a phone), the rest and those that can't behind its More…. */
export function tabHeadCommandsOf(
  commands: readonly Command[],
  isPhone: boolean,
): TabHeadCommands {
  const runnable = commands.filter((c) => c.enabled);
  const shown = runnable.slice(0, isPhone ? 1 : TAB_BUTTONS_SHOWN);
  return {
    buttons: shown,
    more: runnable.slice(shown.length),
    unavailable: commands.filter((c) => !c.enabled),
  };
}
